import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  TextField,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface VenueSummaryViewProps {
  venue: {
    id: string;
    editorialSummary?: string;
  };        
  onVenueUpdate?: (updatedVenue: any) => void;
}

export const VenueSummaryView: React.FC<VenueSummaryViewProps> = ({ 
  venue, 
  onVenueUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState(venue.editorialSummary || '');
  const [isExpanded, setIsExpanded] = useState(false);

  const TRUNCATE_LENGTH = 100;

  // Calculate the number of rows needed based on content
  const calculateRows = (text: string) => {
    const lines = text.split('\n').length;
    const estimatedLines = Math.ceil(text.length / 80); // Roughly 80 chars per line
    return Math.max(lines, estimatedLines, 3); // Minimum 3 rows
  };

  const handleSave = () => {
    if (onVenueUpdate) {
      onVenueUpdate({
        ...venue,
        editorialSummary: editedSummary
      });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedSummary(venue.editorialSummary || '');
    setIsEditing(false);
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Function to truncate summary for display
  const shouldTruncate = venue.editorialSummary && venue.editorialSummary.length > TRUNCATE_LENGTH;
  const displaySummary = shouldTruncate && !isExpanded 
    ? venue.editorialSummary!.substring(0, TRUNCATE_LENGTH) + '...'
    : venue.editorialSummary;

  if (!venue.editorialSummary && !isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No summary
        </Typography>
        <IconButton 
          size="small" 
          onClick={() => setIsEditing(true)}
          sx={{ ml: 1 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      {isEditing ? (
        <Box>
          <TextField
            multiline
            minRows={3}
            maxRows={15}
            size="small"
            value={editedSummary}
            onChange={(e) => setEditedSummary(e.target.value)}
            placeholder="Enter editorial summary"
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <IconButton size="small" onClick={handleSave} color="primary">
              <CheckIcon />
            </IconButton>
            <IconButton size="small" onClick={handleCancel} color="error">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2"
                sx={{ 
                  cursor: shouldTruncate ? 'pointer' : 'default',
                  lineHeight: 1.4
                }}
                onClick={shouldTruncate ? handleToggleExpanded : undefined}
              >
                {displaySummary}
              </Typography>
              {shouldTruncate && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Tooltip title={isExpanded ? "Show less" : "Show more"}>
                    <IconButton 
                      size="small" 
                      onClick={handleToggleExpanded}
                      sx={{ p: 0.25 }}
                    >
                      {isExpanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      ml: 0.5, 
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                    onClick={handleToggleExpanded}
                  >
                    {isExpanded ? "Show less" : "Show more"}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton 
              size="small" 
              onClick={() => setIsEditing(true)}
              sx={{ ml: 1, alignSelf: 'flex-start' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};
